// Skirmish setup screen (M4, DESIGN.md §8.2, §10 — flaw fix #5): map size,
// seed, mirror fairness, faction, AI difficulty, and win condition, rendered
// as a pre-game overlay. Plain TS like the rest of the HUD.

import factionsJson from '../../data/factions.json';
import type { AiDifficulty, GameConfig, WinCondition } from '../shared/protocol';
import type { MapSizeName } from '../shared/constants';

export function showSetup(
  root: HTMLElement,
  defaults: GameConfig,
  onStart: (config: GameConfig) => void,
): void {
  const el = document.createElement('div');
  el.id = 'setup';
  const factionOpts = (sel: number): string =>
    factionsJson.factions
      .map((f, i) => `<option value="${i}" ${i === sel ? 'selected' : ''}>${f.name} — ${f.fantasy}</option>`)
      .join('');
  el.innerHTML = `
    <div class="setup-panel">
      <div class="setup-title">PORTAGE</div>
      <div class="setup-sub">skirmish setup</div>
      <label>Map size
        <select id="su-size">
          <option value="small">Small (128²)</option>
          <option value="medium" selected>Medium (192²)</option>
          <option value="large">Large (256²)</option>
        </select>
      </label>
      <label>Seed
        <span class="row-inline">
          <input id="su-seed" type="text" value="${defaults.seed}" size="10" />
          <button id="su-rand">🎲</button>
        </span>
      </label>
      <label>Mirrored map <input id="su-mirror" type="checkbox" ${defaults.mirror ? 'checked' : ''} /></label>
      <label>Your faction
        <select id="su-faction">${factionOpts(defaults.playerFaction)}</select>
      </label>
      <label>Opponent
        <select id="su-ai">
          <option value="none">None (sandbox)</option>
          <option value="easy">Easy AI</option>
          <option value="normal" selected>Normal AI</option>
          <option value="hard">Hard AI</option>
        </select>
      </label>
      <label>Enemy faction
        <select id="su-efaction">${factionOpts(defaults.enemyFaction)}</select>
      </label>
      <label>Win condition
        <select id="su-win">
          <option value="conquest" selected>Conquest (destroy the enemy HQ)</option>
          <option value="monument">Monument race (5 stages)</option>
          <option value="score">Score at time limit</option>
        </select>
      </label>
      <label id="su-minutes-row" style="display:none">Time limit
        <select id="su-minutes">
          <option value="20">20 min</option>
          <option value="40" selected>40 min</option>
          <option value="60">60 min</option>
        </select>
      </label>
      <button id="su-start" class="setup-start">Start</button>
    </div>`;
  root.appendChild(el);

  const q = <T extends HTMLElement>(id: string): T => el.querySelector<T>(`#${id}`)!;
  q<HTMLSelectElement>('su-size').value = defaults.mapSize;
  q<HTMLSelectElement>('su-ai').value = defaults.aiDifficulty;
  q<HTMLSelectElement>('su-win').value = defaults.winCondition;
  q<HTMLSelectElement>('su-minutes').value = String(defaults.scoreMinutes);

  const winSel = q<HTMLSelectElement>('su-win');
  const minutesRow = q<HTMLElement>('su-minutes-row');
  const syncMinutes = (): void => {
    minutesRow.style.display = winSel.value === 'score' ? '' : 'none';
  };
  winSel.addEventListener('change', syncMinutes);
  syncMinutes();

  q<HTMLButtonElement>('su-rand').addEventListener('click', () => {
    q<HTMLInputElement>('su-seed').value = String((Date.now() & 0x7fffffff) >>> 0);
  });

  q<HTMLButtonElement>('su-start').addEventListener('click', () => {
    const seedRaw = Number.parseInt(q<HTMLInputElement>('su-seed').value.trim(), 10);
    const config: GameConfig = {
      seed: Number.isFinite(seedRaw) ? seedRaw >>> 0 : (Date.now() & 0x7fffffff) >>> 0,
      mapSize: q<HTMLSelectElement>('su-size').value as MapSizeName,
      mirror: q<HTMLInputElement>('su-mirror').checked,
      playerFaction: Number(q<HTMLSelectElement>('su-faction').value),
      enemyFaction: Number(q<HTMLSelectElement>('su-efaction').value),
      aiDifficulty: q<HTMLSelectElement>('su-ai').value as AiDifficulty,
      winCondition: q<HTMLSelectElement>('su-win').value as WinCondition,
      scoreMinutes: Number(q<HTMLSelectElement>('su-minutes').value),
    };
    el.remove();
    onStart(config);
  });
}
